<script>
    import {network, store} from '../stores.js';
    import TopMenu from '../components/TopMenu.svelte';
    import {Link, navigate} from 'svelte-routing';
    import {fly, fade} from 'svelte/transition';
    import storage from '../storage.js';

	const user = store('credential', 'getAuth');
    const axis = store('axis', '', false);

    // Wizard state
    let step = 1;
    let orderId = null;
    let orderVid = '16'; // New order status
    let direction = 1; // 1 = forward, -1 = backward
    let scrollY = 0; // For scroll to top on step change

    // Validation state
    let errors = {};

    // Saved contacts
    let savedContacts = [];
    let selectedContactIndex = -1;

    // Success notification state
    let showSuccess = false;

    // Load saved contacts at initialization
    try {
        const saved = localStorage.getItem('saved_contacts');
        savedContacts = saved ? JSON.parse(saved) : [];
    } catch (e) {
        savedContacts = [];
    }

    // Load last delivery at initialization
    let lastDelivery = null;
    try {
        const saved = localStorage.getItem('last_delivery');
        lastDelivery = saved ? JSON.parse(saved) : null;
    } catch (e) {
        lastDelivery = null;
    }

    // Helper function to check if value is empty or whitespace only
    const isEmpty = (value) => {
        return !value || (typeof value === 'string' && value.trim() === '');
    };

    // Helper function to format name for display (first word + first letter of last name)
    const formatContactName = (fullName) => {
        if (!fullName) return '';
        const parts = fullName.trim().split(/\s+/);
        if (parts.length === 1) return parts[0];
        if (parts.length === 2) return parts[0] + ' ' + parts[1].charAt(0) + '.';
        return parts[0] + ' ' + parts[parts.length - 1].charAt(0) + '.';
    };

    // Delete a saved contact
    const deleteContact = (index, event) => {
        event.stopPropagation(); // Prevent chip selection
        
        if (index >= 0 && index < savedContacts.length) {
            savedContacts = savedContacts.filter((_, i) => i !== index);
            
            // Update localStorage
            try {
                localStorage.setItem('saved_contacts', JSON.stringify(savedContacts));
            } catch (e) {
                // Fallback if localStorage fails
            }
            
            // Clear selection if deleted contact was selected
            if (selectedContactIndex === index) {
                selectedContactIndex = -1;
            } else if (selectedContactIndex > index) {
                // Adjust selection index if deleted contact was before selected one
                selectedContactIndex--;
            }
        }
    };

    // Save contact to localStorage
    const saveContact = () => {
        if (isEmpty(nameKontakt) || isEmpty(telefonKontakt)) {
            return; // Don't save if required fields are empty
        }

        const newContact = {
            name: nameKontakt,
            phone: telefonKontakt,
            email: other1Kontakt || '',
            address: adresKontakt || '',
            extra: other2Kontakt || ''
        };

        // Check for duplicate by phone
        const existingIndex = savedContacts.findIndex(c => c.phone === newContact.phone);
        
        if (existingIndex >= 0) {
            // Update existing contact
            savedContacts[existingIndex] = newContact;
        } else {
            // Add new contact
            savedContacts.unshift(newContact);
            
            // Keep maximum 10 contacts
            if (savedContacts.length > 10) {
                savedContacts = savedContacts.slice(0, 10);
            }
        }

        // Save to localStorage
        try {
            localStorage.setItem('saved_contacts', JSON.stringify(savedContacts));
        } catch (e) {
            // Fallback if localStorage fails
        }
    };

    // Select a saved contact
    const selectContact = (index) => {
        if (index >= 0 && index < savedContacts.length) {
            const contact = savedContacts[index];
            nameKontakt = contact.name || '';
            telefonKontakt = contact.phone || '';
            other1Kontakt = contact.email || '';
            adresKontakt = contact.address || '';
            other2Kontakt = contact.extra || '';
            selectedContactIndex = index;
            
            // Clear any errors when selecting a contact
            if (errors.nameKontakt) errors.nameKontakt = false;
            if (errors.telefonKontakt) errors.telefonKontakt = false;
        }
    };

    // Step 1: Order info
    const fetchMaterials = async () => {
        return await network.getMaterial();
    }
    let materials = fetchMaterials();
    
    let vin = '';
    let name = '';
    let description = '';
    let problem = '';
    let material = '';

    // Step 2: Contact info
    let nameKontakt = '';
    let telefonKontakt = '';
    let other1Kontakt = ''; // email
    let adresKontakt = '';
    let other2Kontakt = ''; // доп.инфо
    let hasPrefilled = false; // Track if we've already pre-filled

    // Pre-fill contact fields: from last saved contact if available, otherwise from profile
    $: if ($user && !hasPrefilled && step === 1) {
        if (savedContacts && savedContacts.length > 0) {
            // Pre-fill from last saved contact (last element in array)
            const lastContact = savedContacts[savedContacts.length - 1];
            nameKontakt = lastContact.name || '';
            telefonKontakt = lastContact.phone || '';
            other1Kontakt = lastContact.email || '';
            adresKontakt = lastContact.address || '';
            other2Kontakt = lastContact.extra || '';
            selectedContactIndex = savedContacts.length - 1; // Mark as selected
            hasPrefilled = true;
        } else if ($user.name) {
            // Fallback to profile data
            nameKontakt = $user.name || '';
            telefonKontakt = $user.tel || '';
            other1Kontakt = $user.email || '';
            hasPrefilled = true;
        }
    }

    // Step 3: Delivery info
    const fetchTV = async () => {
        const query = `EXEC getTV @idSklad = 0`;
        return await network.proxy(query);
    }
    let tvList = fetchTV();
    
    // Initialize delivery fields with saved values from localStorage
    let tv = lastDelivery?.pickup_point || '';
    let delivery = lastDelivery?.delivery_type || '';

    // Validation functions
    const validateStep1 = () => {
        errors = {};
        
        if (isEmpty(material) || material === '') {
            errors.material = true;
        }
        if (isEmpty(vin)) {
            errors.vin = true;
        }
        if (isEmpty(name)) {
            errors.name = true;
        }
        if (isEmpty(description)) {
            errors.description = true;
        }
        if (isEmpty(problem)) {
            errors.problem = true;
        }
        
        return Object.keys(errors).length === 0;
    };

    const validateStep2 = () => {
        errors = {};
        
        if (isEmpty(nameKontakt)) {
            errors.nameKontakt = true;
        }
        if (isEmpty(telefonKontakt)) {
            errors.telefonKontakt = true;
        }
        
        return Object.keys(errors).length === 0;
    };

    const validateStep3 = () => {
        errors = {};
        
        // Validate pickup point - check for empty, empty string, or default "Выберете" option
        if (isEmpty(tv) || tv === '' || tv === 'Выберете') {
            errors.tv = true;
        }
        // Validate delivery type - check for empty, empty string, or default "Выберете" option
        if (isEmpty(delivery) || delivery === '' || delivery === 'Выберете') {
            errors.delivery = true;
        }
        
        return Object.keys(errors).length === 0;
    };

    // Step 1: Create order
    const handleStep1Next = async () => {
        // Validate step 1
        if (!validateStep1()) {
            scrollY = 0; // Scroll to top to show errors
            return;
        }

        let list = await materials;
        
        // Convert material to number for comparison (idTovar might be numeric)
        let materialId = material ? +material : null;
        let item = list.find((el) => el.idTovar == materialId || el.idTovar === material);
        
        // Validate that material is selected and item is found
        if (!material || !item) {
            errors.material = true;
            scrollY = 0;
            return; // Don't proceed if material not selected
        }
    
        let result = await network.createTovar({
            name,
            vin,
            description,
            problem,
            material,
            item: item.name || '',
            user_sklad: +$user.idSklad,
            user_kontr: +$user.idkontr,
            user_id: +$user.id,
        });

        // Validate API response before proceeding
        if (!result || !result.nomDoc) {
            return; // Don't proceed if order creation failed
        }

        // Send chat message
        network.sendChatMessage(+result.nomDoc, +$user.id, $user.name, problem, 'text');
    
        // Save order ID for next steps
        orderId = result.nomDoc;

        // Clear errors on successful step
        errors = {};

        // Handle axis mode
        if ($axis) {
            let params = {
                name,
                vin,
                description,
                problem,
                material,
                item: item.name || '',
                user_sklad: +$user.idSklad,
                user_kontr: +$user.idkontr,
                user_id: +$user.id,
            };
        
            window.top.postMessage('add_item::' + JSON.stringify(params), '*');
        } else {
            // Advance to step 2
            direction = 1;
            step = 2;
            scrollY = 0; // Scroll to top
        }
    };

    // Step 2: Save contact
    const handleStep2Next = async () => {
        // Validate step 2
        if (!validateStep2()) {
            scrollY = 0; // Scroll to top to show errors
            return;
        }

        await network.saveKontakt({
            nom: orderId,
            vid: orderVid,
            idKontakt: '', // New contact, no ID
            nameKontakt,
            telefonKontakt,
            adresKontakt,
            other1Kontakt,
            other2Kontakt,
            actualnost: 1,
            user_kontr: +$user.idkontr,
        });

        // Clear errors on successful step
        errors = {};

        direction = 1;
        step = 3;
        scrollY = 0; // Scroll to top
    };

    const handleStep2Back = () => {
        errors = {}; // Clear errors when going back
        selectedContactIndex = -1; // Clear selected contact
        direction = -1;
        step = 1;
        scrollY = 0; // Scroll to top
    };

    // Step 3: Save delivery and finish
    const handleStep3Finish = async () => {
        // Validate step 3
        if (!validateStep3()) {
            scrollY = 0; // Scroll to top to show errors
            return;
        }

        await network.saveDelivery({
            nom: orderId,
            vid: orderVid,
            tv: tv,
            dost: delivery,
        });

        // Save contact after successful order creation
        saveContact();

        // Save delivery data to localStorage
        // Save the exact value from the select (tv contains item.nameTV which matches option value)
        try {
            const deliveryData = {
                pickup_point: tv || '',
                delivery_type: delivery || ''
            };
            localStorage.setItem('last_delivery', JSON.stringify(deliveryData));
        } catch (e) {
            // Fallback if localStorage fails
        }

        if ($axis) {
            // In axis mode, don't navigate
            return;
        } else {
            // Show success notification and redirect
            showSuccess = true;
            
            // Navigate to dashboard after 2500ms
            setTimeout(() => {
                navigate('/dashboard');
            }, 2500);
        }
    };

    const handleStep3Back = () => {
        errors = {}; // Clear errors when going back
        direction = -1;
        step = 2;
        scrollY = 0; // Scroll to top
    };
</script>

<svelte:window bind:scrollY={scrollY} />

<TopMenu title="Новая заявка">
    <Link to="/dashboard" class="headerButton goBack" slot="left">
        <ion-icon name="chevron-back-outline" role="img" class="md hydrated" aria-label="chevron back outline"></ion-icon>
    </Link>
</TopMenu>

<div id="appCapsule">
    <!-- Progress Indicator -->
    <div class="wizard-progress">
        <div class="progress-container">
            <!-- Step 1 -->
            <div class="progress-step">
                <div class="step-circle" class:active={step === 1} class:completed={step > 1}>
                    {#if step > 1}
                        <ion-icon name="checkmark" role="img" class="md hydrated"></ion-icon>
                    {:else}
                        <span class="step-number">1</span>
                    {/if}
                </div>
                <div class="step-label" class:active={step === 1} class:completed={step > 1}>Заявка</div>
            </div>
            
            <!-- Connector Line 1 -->
            <div class="progress-line" class:completed={step > 1}></div>
            
            <!-- Step 2 -->
            <div class="progress-step">
                <div class="step-circle" class:active={step === 2} class:completed={step > 2}>
                    {#if step > 2}
                        <ion-icon name="checkmark" role="img" class="md hydrated"></ion-icon>
                    {:else}
                        <span class="step-number">2</span>
                    {/if}
                </div>
                <div class="step-label" class:active={step === 2} class:completed={step > 2}>Контакт</div>
            </div>
            
            <!-- Connector Line 2 -->
            <div class="progress-line" class:completed={step > 2}></div>
            
            <!-- Step 3 -->
            <div class="progress-step">
                <div class="step-circle" class:active={step === 3} class:completed={false}>
                    <span class="step-number">3</span>
                </div>
                <div class="step-label" class:active={step === 3}>Доставка</div>
            </div>
        </div>
    </div>

    <div class="section mt-2 mb-5 wizard-content">
        <!-- Step 1: Order Info -->
        {#if step === 1}
        <div class="step-content" transition:fly={{ x: direction * 300, duration: 300 }}>
        <div class="form-group boxed">
            <div class="input-wrapper">
                <label class="form-label" for="material">Агрегат</label>
                    <select 
                        class="form-control form-select" 
                        id="material" 
                        class:invalid={errors.material}
                        bind:value={material}
                        on:change={() => { if (errors.material) errors.material = false; }}
                    >
                    {#await materials}
                        <option selected disabled>Загрузка..</option>
                    {:then list}
                            <option value="" selected disabled>Выберете</option>
                        {#each list as item}
                            <option value="{item.idTovar}">{item.name}</option>
                        {/each}
                    {/await}
                </select>
                    {#if errors.material}
                        <span class="error-message">Обязательное поле</span>
                    {/if}
                </div>
        </div>

        <div class="form-group boxed">
            <div class="input-wrapper">
                <label class="form-label" for="vin">VIN номер</label>
                    <input 
                        type="text" 
                        class="form-control" 
                        id="vin" 
                        class:invalid={errors.vin}
                        placeholder="0432193757" 
                        bind:value={vin} 
                        autocomplete="off"
                        on:input={() => { if (errors.vin) errors.vin = false; }}
                    >
                    {#if errors.vin}
                        <span class="error-message">Обязательное поле</span>
                    {/if}
                </div>
        </div>
        
        <div class="form-group boxed">
            <div class="input-wrapper">
                <label class="form-label" for="item">Наименование агрегата</label>
                    <input 
                        type="text" 
                        class="form-control" 
                        id="item" 
                        class:invalid={errors.name}
                        placeholder="Механические форсунки Bosch" 
                        bind:value={name} 
                        autocomplete="off"
                        on:input={() => { if (errors.name) errors.name = false; }}
                    >
                    {#if errors.name}
                        <span class="error-message">Обязательное поле</span>
                    {/if}
                </div>
        </div>
    
        <div class="form-group boxed">
            <div class="input-wrapper">
                <label class="form-label" for="details">Опись комплектующих</label>
                    <textarea 
                        id="details" 
                        rows="2" 
                        class="form-control" 
                        class:invalid={errors.description}
                        placeholder="4 штуки" 
                        bind:value={description}
                        on:input={() => { if (errors.description) errors.description = false; }}
                    ></textarea>
                    {#if errors.description}
                        <span class="error-message">Обязательное поле</span>
                    {/if}
                </div>
        </div>
    
        <div class="form-group boxed">
            <div class="input-wrapper">
                <label class="form-label" for="note">Описание проблемы</label>
                    <textarea 
                        id="note" 
                        rows="2" 
                        class="form-control" 
                        class:invalid={errors.problem}
                        placeholder="Необходима проверка" 
                        bind:value={problem}
                        on:input={() => { if (errors.problem) errors.problem = false; }}
                    ></textarea>
                    {#if errors.problem}
                        <span class="error-message">Обязательное поле</span>
                    {/if}
                </div>
            </div>
        </div>
        {/if}

        <!-- Step 2: Contact -->
        {#if step === 2}
        <div class="step-content" transition:fly={{ x: direction * 300, duration: 300 }}>
            <!-- Saved Contacts Section -->
            {#if savedContacts.length > 0}
            <div class="saved-contacts-section">
                <div class="saved-contacts-label">Сохранённые контакты</div>
                <div class="saved-contacts-container">
                    {#each savedContacts as contact, index}
                        <button
                            type="button"
                            class="contact-chip"
                            class:active={selectedContactIndex === index}
                            on:click={() => selectContact(index)}
                        >
                            <span class="chip-text">{formatContactName(contact.name)} · {contact.phone}</span>
                            <button
                                type="button"
                                class="chip-delete"
                                on:click|stopPropagation={(e) => deleteContact(index, e)}
                            >
                                ×
                            </button>
                        </button>
                    {/each}
                </div>
            </div>
            {/if}

            <div class="form-group boxed">
                <div class="input-wrapper">
                    <label class="form-label" for="nameKontakt">Контакт</label>
                    <input 
                        type="text" 
                        class="form-control" 
                        id="nameKontakt" 
                        class:invalid={errors.nameKontakt}
                        bind:value={nameKontakt} 
                        placeholder="Название контакта"
                        on:input={() => { 
                            if (errors.nameKontakt) errors.nameKontakt = false;
                            // Clear selected contact when user manually edits
                            if (selectedContactIndex >= 0) selectedContactIndex = -1;
                        }}
                    >
                    {#if errors.nameKontakt}
                        <span class="error-message">Обязательное поле</span>
                    {/if}
                </div>
            </div>

            <div class="form-group boxed">
                <div class="input-wrapper">
                    <label class="form-label" for="telefonKontakt">Телефон</label>
                    <input 
                        type="text" 
                        class="form-control" 
                        id="telefonKontakt" 
                        class:invalid={errors.telefonKontakt}
                        bind:value={telefonKontakt} 
                        placeholder="+7 (999) 123-45-67"
                        on:input={() => { 
                            if (errors.telefonKontakt) errors.telefonKontakt = false;
                            // Clear selected contact when user manually edits
                            if (selectedContactIndex >= 0) selectedContactIndex = -1;
                        }}
                    >
                    {#if errors.telefonKontakt}
                        <span class="error-message">Обязательное поле</span>
                    {/if}
                </div>
            </div>

            <div class="form-group boxed">
                <div class="input-wrapper">
                    <label class="form-label" for="other1Kontakt">Электронная почта</label>
                    <input 
                        type="text" 
                        class="form-control" 
                        id="other1Kontakt" 
                        bind:value={other1Kontakt} 
                        placeholder="email@example.com"
                        on:input={() => {
                            // Clear selected contact when user manually edits
                            if (selectedContactIndex >= 0) selectedContactIndex = -1;
                        }}
                    >
                </div>
            </div>

            <div class="form-group boxed">
                <div class="input-wrapper">
                    <label class="form-label" for="adresKontakt">Адрес</label>
                    <textarea 
                        maxlength="1000" 
                        class="form-control" 
                        id="adresKontakt" 
                        bind:value={adresKontakt} 
                        rows="2" 
                        placeholder="Адрес доставки"
                        on:input={() => {
                            // Clear selected contact when user manually edits
                            if (selectedContactIndex >= 0) selectedContactIndex = -1;
                        }}
                    ></textarea>
            </div>
        </div>
    
            <div class="form-group boxed">
                <div class="input-wrapper">
                    <label class="form-label" for="other2Kontakt">Доп.инфо</label>
                    <textarea 
                        maxlength="1000" 
                        class="form-control" 
                        id="other2Kontakt" 
                        bind:value={other2Kontakt} 
                        rows="2" 
                        placeholder="Дополнительная информация"
                        on:input={() => {
                            // Clear selected contact when user manually edits
                            if (selectedContactIndex >= 0) selectedContactIndex = -1;
                        }}
                    ></textarea>
                </div>
            </div>
        </div>
        {/if}

        <!-- Step 3: Delivery -->
        {#if step === 3}
        <div class="step-content" transition:fly={{ x: direction * 300, duration: 300 }}>
            <div class="form-group boxed">
                <div class="input-wrapper">
                    <label class="form-label" for="tv">Точка приёма и выдачи</label>
                    <select 
                        class="form-control form-select" 
                        id="tv" 
                        class:invalid={errors.tv}
                        bind:value={tv}
                        on:change={() => { if (errors.tv) errors.tv = false; }}
                    >
                        {#await tvList}
                            <option selected disabled>Загрузка..</option>
                        {:then list}
                            <option value="" selected disabled>Выберете</option>
                            {#each list as item}
                                <option value="{item.nameTV}">{item.nameTV}: {item.adresTV}</option>
                            {/each}
                        {/await}
                    </select>
                    {#if errors.tv}
                        <span class="error-message">Обязательное поле</span>
                    {/if}
                </div>
            </div>

            <div class="form-group boxed">
                <div class="input-wrapper">
                    <label class="form-label" for="delivery">Тип доставки</label>
                    <select 
                        class="form-control form-select" 
                        id="delivery" 
                        class:invalid={errors.delivery}
                        bind:value={delivery}
                        on:change={() => { if (errors.delivery) errors.delivery = false; }}
                    >
                        <option value="" selected disabled>Выберете</option>
                        {#each ['Самостоятельно', 'Сторонний курьер', 'Доставка БашДизель'] as item}
                            <option value="{item}">{item}</option>
                        {/each}
                    </select>
                    {#if errors.delivery}
                        <span class="error-message">Обязательное поле</span>
                    {/if}
                </div>
            </div>
        </div>
        {/if}
    </div>
</div>
<!-- Success Overlay -->
{#if showSuccess}
<div class="success-overlay" transition:fade={{ duration: 300 }}>
    <div class="success-card">
        <ion-icon name="build" class="repair-icon"></ion-icon>
        <span class="check">✓</span>
        <p class="title">Заявка создана!</p>
        <p class="subtitle">Перенаправляем в ваши заявки...</p>
    </div>
</div>
{/if}

<!-- Fixed Button Bar -->
<div class="wizard-button-bar">
    {#if step === 1}
        <button on:click={handleStep1Next} class="btn btn-primary btn-block btn-lg">Далее</button>
    {:else if step === 2}
        <div class="row">
            <div class="col-6">
                <button on:click={handleStep2Back} class="btn btn-secondary btn-block btn-lg">Назад</button>
            </div>
            <div class="col-6">
                <button on:click={handleStep2Next} class="btn btn-primary btn-block btn-lg">Далее</button>
            </div>
        </div>
    {:else if step === 3}
        <div class="row">
            <div class="col-6">
                <button on:click={handleStep3Back} class="btn btn-secondary btn-block btn-lg">Назад</button>
            </div>
            <div class="col-6">
                <button on:click={handleStep3Finish} class="btn btn-success btn-block btn-lg">Готово</button>
            </div>
        </div>
    {/if}
</div>

<style>
    .wizard-progress {
        padding: 20px 15px;
        background: #fff;
    }

    .progress-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        max-width: 100%;
        margin: 0 auto;
    }

    .progress-step {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
        position: relative;
    }

    .step-circle {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: #e5e7eb;
        border: 2px solid #e5e7eb;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        z-index: 2;
        transition: all 0.3s ease;
    }

    .step-circle.active {
        background: #2563eb;
        border-color: #2563eb;
    }

    .step-circle.completed {
        background: #2563eb;
        border-color: #2563eb;
    }

    .step-number {
        font-size: 14px;
        font-weight: bold;
        color: #6b7280;
    }

    .step-circle.active .step-number {
        color: #fff;
    }

    .step-circle.completed ion-icon {
        color: #fff;
        font-size: 18px;
    }

    .step-label {
        margin-top: 8px;
        font-size: 12px;
        color: #9ca3af;
        text-align: center;
        transition: color 0.3s ease;
    }

    .step-label.active {
        color: #2563eb;
        font-weight: bold;
    }

    .step-label.completed {
        color: #2563eb;
    }

    .progress-line {
        flex: 1;
        height: 2px;
        background: #e5e7eb;
        margin: 0 8px;
        margin-top: -20px;
        position: relative;
        z-index: 1;
        transition: background 0.3s ease;
    }

    .progress-line.completed {
        background: #2563eb;
    }

    .wizard-content {
        padding-bottom: 80px;
    }

    .step-content {
        width: 100%;
    }

    .wizard-button-bar {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 16px;
        background: #fff;
        border-top: 1px solid #eee;
        z-index: 10;
    }

    .form-control.invalid,
    .form-select.invalid {
        border: 1.5px solid #ef4444;
        background-color: #fff5f5;
        border-radius: 8px;
    }

    .error-message {
        color: #ef4444;
        font-size: 12px;
        margin-top: 4px;
        display: block;
    }

    .saved-contacts-section {
        margin-bottom: 20px;
    }

    .saved-contacts-label {
        font-size: 13px;
        font-weight: 600;
        color: #374151;
        margin-bottom: 12px;
    }

    .saved-contacts-container {
        display: flex;
        gap: 8px;
        overflow-x: auto;
        padding-bottom: 8px;
        margin-bottom: 16px;
        -webkit-overflow-scrolling: touch;
    }

    .saved-contacts-container::-webkit-scrollbar {
        display: none;
    }

    .saved-contacts-container {
        -ms-overflow-style: none;
        scrollbar-width: none;
    }

    .contact-chip {
        background: #f0f4ff;
        border: 1px solid #2563eb;
        border-radius: 20px;
        padding: 6px 14px;
        font-size: 13px;
        white-space: nowrap;
        color: #2563eb;
        cursor: pointer;
        transition: all 0.2s ease;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: 4px;
    }

    .contact-chip:hover {
        background: #e0e7ff;
    }

    .contact-chip.active {
        background: #2563eb;
        color: #fff;
    }

    .chip-text {
        flex: 1;
    }

    .chip-delete {
        background: none;
        border: none;
        color: inherit;
        font-size: 14px;
        line-height: 1;
        padding: 0 0 0 6px;
        cursor: pointer;
        opacity: 0.7;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .chip-delete:hover {
        opacity: 1;
    }

    .success-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background: rgba(15, 23, 42, 0.55);
        backdrop-filter: blur(18px);
        -webkit-backdrop-filter: blur(18px);
    }

    .success-card {
        background: rgba(255, 255, 255, 0.15);
        border: 1px solid rgba(255, 255, 255, 0.3);
        border-radius: 24px;
        padding: 40px 32px;
        text-align: center;
        max-width: 320px;
        width: 90%;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    }

    .repair-icon {
        display: block;
        font-size: 64px;
        color: rgba(255, 255, 255, 0.85);
        margin-bottom: 12px;
        animation: toolWobble 1.2s ease-in-out infinite alternate;
    }

    .check {
        font-size: 56px;
        color: #4ade80;
        display: block;
        margin-bottom: 16px;
        animation: popIn 0.5s ease-out;
    }

    .title {
        font-size: 22px;
        font-weight: 700;
        color: #fff;
        margin-bottom: 8px;
    }

    .subtitle {
        font-size: 14px;
        color: rgba(255, 255, 255, 0.75);
        margin: 0;
    }

    @keyframes toolWobble {
        from {
            transform: rotate(-15deg) scale(1);
        }
        to {
            transform: rotate(15deg) scale(1.08);
        }
    }

    @keyframes popIn {
        0% {
            transform: scale(0);
            opacity: 0;
        }
        70% {
            transform: scale(1.2);
        }
        100% {
            transform: scale(1);
            opacity: 1;
        }
    }

    @media (max-width: 390px) {
        .wizard-progress {
            padding: 15px 10px;
        }

        .step-circle {
            width: 28px;
            height: 28px;
        }

        .step-number {
            font-size: 12px;
        }

        .step-label {
            font-size: 11px;
        }
    }
</style>
